// Previous/Next navigation between events on the event page.
//
// Whatever list the user opened an event from (My Events in any sort/view,
// Shared with Me, search results, the sidebar) passes its events, in display
// order, through react-router's location state. EventDetail reads it back to
// offer "‹ 3 of 12 ›". History state survives a page refresh, but not a pasted
// link — with no state, the event page simply shows no Previous/Next.

export interface EventNavItem {
  id: string;
  title: string;
}

export interface EventNavState {
  eventNav: { items: EventNavItem[] };
}

export function readEventNav(state: unknown): EventNavItem[] | null {
  const items = (state as Partial<EventNavState> | null)?.eventNav?.items;
  return Array.isArray(items) && items.length > 1 ? items : null;
}

/**
 * The events in the same nav scope as `el`, in DOM (= display) order. Reading
 * the DOM keeps this in step with whatever sort/grouping/filter the list is
 * currently rendering, without every view re-deriving its own ordering.
 */
export function collectEventNavItems(el: Element): EventNavItem[] {
  const scope = el.closest('[data-event-nav-scope]');
  if (!scope) return [];
  const seen = new Set<string>();
  const items: EventNavItem[] = [];
  scope.querySelectorAll<HTMLElement>('[data-event-nav-id]').forEach(node => {
    const id = node.dataset.eventNavId!;
    if (seen.has(id)) return;
    seen.add(id);
    items.push({ id, title: node.dataset.eventNavTitle ?? '' });
  });
  return items;
}

/** Ctrl/Cmd/Shift/middle clicks keep their native open-in-new-tab behavior. */
export function isPlainLeftClick(e: { button: number; metaKey: boolean; ctrlKey: boolean; shiftKey: boolean; altKey: boolean }): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}
